import { RootStore } from "@lib/store";
import { BasicLitElement } from "../basic";
import { calendarContextMenu, eventActionsMenuPopup, popupContextMenuEvent, popupEventsList, popupSelectEvent, scheduleTemplate } from "./templates";
import { Lambda, observe } from "mobx";
import moment from "moment";
import { Calendar as VanillaCalendar } from "vanilla-calendar-pro";

// Class name.
// eslint-disable-next-line @typescript-eslint/naming-convention
import Calendar, { EventObject, TZDate } from "@toast-ui/calendar";
import { Options } from "@toast-ui/calendar/types/types/options";
import { ScheduleCalendarStore, ScheduleCalendarType } from "@lib/store/src/scheduleCalendar";
import { MsScheduleApi } from "@lib/api";
import { ModalManager } from "./modals/modal-manager";
import { AddScheduleModal } from "./modals/addScheduleModal/addScheduleModal";
import { UiModal } from "../common/modal/modal";
import { CSSResultGroup, PropertyValues, TemplateResult } from "lit";
import { property, query, state } from "lit/decorators.js";
import { scheduleCss } from "./style";
import { UiFloatPopover } from "../common/popover";
import { EditScheduleModal } from "./modals/editScheduleModal/editScheduleModal";
import { HolidaysModal } from "./modals/HolidaysModal/HolidaysModal";
import { RRuleSet } from "rrule";
import { mapFromRRuleFrequency, ScheduleRecurrence } from "./types";
import { compareScheduleItems, isNonRepeatableItem, Schedule, ScheduleItem } from "@lib/types";
import { convertDateToUtc } from "./utils/convertDateToUtc";
import { showConfirmationPopup } from "./modals/confirmationPopup/confirmationPopup";
import { getTemporaryScheduleItemId } from "./utils/getTemporaryScheduleItemId";
import { getSingleItemRRule } from "./utils/getSingleItemRRule";
import { autoPlacement, computePosition } from "@floating-ui/dom";

type EventObjectRaw = {

    /** Start date. */
    start: Date;

    /** Schedule data. */
    scheduleItem: ScheduleItem;
};

/** Calendar event object. */
type CalendarEventObject = {

    /** ID. */
    id: string;

    /** Calendar ID. */
    calendarId: string;

    /** Body. */
    body: string;

    /** Start of event. */
    start: TZDate;

    /** End of event. */
    end: TZDate;

    /** Category. */
    category: EventObject["category"];

    /** Due date class. */
    dueDateClass: string;

    /** Background color. */
    backgroundColor: string;

    /** Raw data of event. */
    raw: EventObjectRaw;

    /** Title. */
    title: string;

    /** Border color. */
    borderColor: string;

    /** Custom style. */
    customStyle: EventObject["customStyle"];
};

const DEFAULT_CALENDAR_OPTIONS: Options = {
    useFormPopup: false,
    useDetailPopup: false,
    isReadOnly: true,
    gridSelection: {
        enableClick: true,
        enableDblClick: true,
    },
    week: {
        eventView: ["time"],
        taskView: false,
        startDayOfWeek: 1,
        dayNames: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
        workweek: true,
    },
    month: {
        startDayOfWeek: 1,
        dayNames: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
        visibleEventCount: 3,
    },
    defaultView: "week",
};

/** Modal ID. */
enum ModalID {
    AddSchedule = "addSchedule",
    EditSchedule = "editScheduleModal",
    Holidays = "holidaysModal",
}

const CALENDAR_TYPE_TO_VIEW_MAP: Readonly<Record<ScheduleCalendarType, Exclude<Options["defaultView"], undefined>>> = {
    today: "day",
    day: "day",
    week: "week",
    workweek: "week",
    month: "month",
};

const MOMENT_LOCALE = "ru";
const DEFAULT_WEEKDAY_NUMBERS = ["0", "6"];

const DOUBLE_CLICK_DELAY_MS = 250;

/** Schedule component. */
export class MSSchedule extends BasicLitElement {

    /** ID of the calendar. */
    public id = "";

    /** Calendar store. */
    private store: ScheduleCalendarStore;

    private disposer: Lambda;

    private calendarSm: VanillaCalendar;

    private calendarLg: Calendar;

    private addScheduleButton: HTMLButtonElement;

    private modalManager = new ModalManager();

    private readonly api = new MsScheduleApi();

    @query("#calendar-sm, .vc")
    private accessor calendarSmRoot: HTMLDivElement;

    @query("#calendar-lg")
    private accessor calendarLgRoot: HTMLDivElement;

    @query(".holidays-button-tooltip")
    private accessor holidaysButtonTooltip: HTMLElement;

    @state()
    protected accessor headerTime = "";

    @state()
    protected accessor schedules: readonly Schedule[] = [];

    @state()
    protected accessor selectedSchedule: Schedule;

    @state()
    protected accessor isScheduleActivationLocked = true;

    @state()
    protected accessor isScheduleEnabled = true;

    @state()
    protected accessor taskEditedWarningType: "none" | "single" | "row" = "none";

    private scheduleItemsPollIntervalId: NodeJS.Timeout;

    private scheduleInfoPollIntervalId: NodeJS.Timeout;

    private readonly exceptionHolidays = new Set<string>();

    private readonly exceptionWorkdays = new Set<string>();

    private eventClickTimeout: NodeJS.Timeout | null = null;

    private lastClickedEventInfo: { id: string; time: Date } | null = null;

    private currentCalendarEvents: CalendarEventObject[] = [];

    @property({ type: Boolean })
    private accessor isglobal = false;

    @property()
    private accessor objectref: string;

    public constructor() {
        super();
        this.checkComponent("ui-modal", UiModal);
        this.initMomentLocale();
        this.generateStore();
        this.onStoreChange();
    }

    /** @inheritdoc */
    public static readonly styles: CSSResultGroup = scheduleCss;

    /** @inheritdoc */
    public render(): TemplateResult {
        return scheduleTemplate.call(this);
    }

    /** @inheritdoc */
    public connectedCallback(): void {
        try {
            super.connectedCallback();
        } catch(e) {
            console.error(e);
        }
    }

    /** @inheritdoc */
    public disconnectedCallback(): void {
        clearInterval(this.scheduleInfoPollIntervalId);
        clearInterval(this.scheduleItemsPollIntervalId);
    }

    /** @inheritdoc */
    public firstUpdated(): void {
        this.initCalendarSm();
        this.initCalendarLg();
        void this.getHolidays();
        void this.getSchedules();
        this.initViewHandler();
        this.initModals();
        this.initActions();
    }

    /** @inheritdoc */
    protected updated(changedProperties: PropertyValues): void {
        if (changedProperties.has("selectedSchedule")) {
            const { selectedSchedule } = this;
            this.store.onChange("schedules", []);
            this.pollScheduleItems(selectedSchedule);
            this.pollScheduleInfo(selectedSchedule);
            this.isScheduleActivationLocked = selectedSchedule.isLocked;
            this.isScheduleEnabled = selectedSchedule.isLocked ?
                selectedSchedule.isEnabledFb :
                selectedSchedule.isEnabledHmi;
        }
    }

    private async getSchedules(): Promise<void> {
        let path = this.isglobal ?
            "" :
            this.objectref;
        let itemId = 0;
        if (!this.isglobal && !path) {
            itemId = window.$sw.ItemSubscription._getParentParam(this, "objectid", true);

            // ServiceWorker is not typed.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            path = window.$sw.ItemSubscription._getOuterPath(this);
        }

        const schedules = await this.api.getSchedules(itemId, path);
        this.schedules = this.sortSchedules(schedules);
        const firstSchedule = this.schedules.at(0);
        if (firstSchedule) {
            this.selectedSchedule = firstSchedule;
        }
    }

    private sortSchedules(schedules: readonly Schedule[]): Schedule[] {
        return [...schedules].sort((schedule1, schedule2) => {
            const result = schedule1.name > schedule2.name ?
                1 :
                -1;
            return result;
        }).sort((schedule1, schedule2) => {
            const isSchedule1Disabled = schedule1.isLocked ?
                schedule1.isEnabledFb :
                schedule1.isEnabledHmi;

            const isSchedule2Disabled = schedule2.isLocked ?
                schedule2.isEnabledFb :
                schedule2.isEnabledHmi;

            if (isSchedule1Disabled && !isSchedule2Disabled) {
                return -1;
            }

            if (!isSchedule1Disabled && isSchedule2Disabled) {
                return 1;
            }

            return 0;
        });
    }

    private async getHolidays(): Promise<void> {
        const exceptionDates = await this.api.getExceptionDates();
        this.store.onChange("holidayExceptions", exceptionDates);
    }

    private async getScheduleItems(schedule: Schedule): Promise<void> {
        const scheduleItems = await this.api.getScheduleTasks({
            id: schedule.scheduleId,
            path: schedule.path,
        });
        const changedItemIDs = this.getChangedScheduleItemIDs(scheduleItems);
        if (changedItemIDs.length > 0) {
            this.store.onChange("schedules", scheduleItems);

            const editModal = this.modalManager.getModal(ModalID.EditSchedule) as EditScheduleModal;

            let changedScheduleId: null | ScheduleItem["id"] = null;
            if (
                editModal.scheduleId !== null &&
                changedItemIDs.includes(editModal.scheduleId)
            ) {
                changedScheduleId = editModal.scheduleId;
            } else if (
                editModal.parentScheduleId !== null &&
                changedItemIDs.includes(editModal.parentScheduleId)
            ) {
                changedScheduleId = editModal.parentScheduleId;
            }

            if (changedScheduleId !== null) {
                const changedScheduleItem = this.store.getScheduleById(changedScheduleId);
                if (changedScheduleItem !== null) {
                    this.modalManager.closeModal(ModalID.EditSchedule);
                    this.taskEditedWarningType = isNonRepeatableItem(changedScheduleItem) ?
                        "single" :
                        "row";
                }
            }
        }
    }

    private getChangedScheduleItemIDs(scheduleItems: readonly ScheduleItem[]): number[] {
        const changedIDs: number[] = [];
        scheduleItems.forEach(item => {
            const existingItem = this.store.getScheduleById(item.id);
            if (existingItem === null) {
                changedIDs.push(item.id);
            } else {
                const isChanged = !compareScheduleItems(item, existingItem);
                if (isChanged) {
                    changedIDs.push(item.id);
                }
            }
        });
        return changedIDs;
    }

    private pollScheduleItems(schedule: Schedule): void {
        clearInterval(this.scheduleItemsPollIntervalId);
        void this.getScheduleItems(schedule);

        this.scheduleItemsPollIntervalId = setInterval(() => {
            void this.getScheduleItems(schedule);
        }, 5000);
    }

    private async getScheduleInfo(schedule: Schedule): Promise<void> {
        const scheduleInfo = await this.api.getScheduleInfo(schedule.scheduleId, schedule.path);
        this.isScheduleActivationLocked = scheduleInfo.isLocked;
        this.isScheduleEnabled = scheduleInfo.isLocked ?
            scheduleInfo.isEnabledFb :
            scheduleInfo.isEnabledHmi;

        this.schedules = this.sortSchedules(this.schedules);
    }

    private pollScheduleInfo(schedule: Schedule): void {
        clearInterval(this.scheduleInfoPollIntervalId);
        void this.getScheduleInfo(schedule);

        this.scheduleInfoPollIntervalId = setInterval(() => {
            void this.getScheduleInfo(schedule);
        }, 1000);
    }

    private generateStore(): void {
        try {
            const { id, state: storeState } = RootStore.scheduleCalendars.add();
            this.id = id;
            this.store = storeState;
        } catch(e) {
            console.error("Error in <generateStore> = ", e);
        }
    }

    private initCalendarSm(): void {
        try {
            this.calendarSm = new VanillaCalendar(
                this.calendarSmRoot,
                {
                    selectedTheme: "light",
                    selectionMonthsMode: "only-arrows",
                    selectionYearsMode: "only-arrows",
                    selectedWeekends: [],
                    enableMonthChangeOnDayClick: false,
                    enableDateToggle: false,
                    onClickArrow: () => {
                        this.updateCalendarHolidays();
                    },
                    onClickDate: self => {
                        const date = self.context.selectedDates[0];
                        this.store.onChange("date", moment(new Date(date)));
                    },
                    styles: {
                        month: "vc-month full-opacity",
                        year: "vc-year full-opacity",
                    },
                    locale: {
                        months: {
                            long: [
                                "Январь",
                                "Февраль",
                                "Март",
                                "Апрель",
                                "Май",
                                "Июнь",
                                "Июль",
                                "Август",
                                "Сентябрь",
                                "Октябрь",
                                "Ноябрь",
                                "Декабрь",
                            ],
                            short: [
                                "Январь",
                                "Февраль",
                                "Март",
                                "Апрель",
                                "Май",
                                "Июнь",
                                "Июль",
                                "Август",
                                "Сентябрь",
                                "Октябрь",
                                "Ноябрь",
                                "Декабрь",
                            ],
                        },
                        weekdays: {
                            long: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
                            short: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
                        },
                    },
                    enableJumpToSelectedDate: true,
                },
            );
            this.calendarSm.init();
        } catch(e) {
            console.error("Error in <initCalendarSm> ", e);
        }
    }

    private initCalendarLg(): void {
        try {
            this.calendarLg = new Calendar(
                this.calendarLgRoot,
                {
                    ...DEFAULT_CALENDAR_OPTIONS,
                    template: {
                        time(event: EventObject) {
                            return `<span class="calendar-time">${moment(
                                (event as CalendarEventObject).raw.start,
                            ).format("HH:mm")}, ${(event as CalendarEventObject).body}</span>`;
                        },
                        weekGridFooterExceed(hiddenEvents) {
                            return `+${hiddenEvents}`;
                        },
                        timegridDisplayPrimaryTime({ time }) {
                            return `${moment(time.toDate()).format("HH:mm")}`;
                        },
                        monthGridHeaderExceed(hiddenEvents) {
                            return (
                                "<span class=\"calendar-month-header-exceed\">" +
                                `${hiddenEvents} ещё ` +
                                "</span>"
                            );
                        },
                        monthDayName(model) {
                            return `<span class="month-day-name">${model.label}</span>`;
                        },
                        monthMoreTitleDate(moreTitle) {
                            const { ymd } = moreTitle;
                            const parsedDate = moment(ymd, "YYYY-MM-DD");

                            return `
                                <span class="toastui-calendar-more-title-date">${parsedDate.format("DD")}</span>
                                <span class="toastui-calendar-more-title-day">${parsedDate.format("ddd")}</span>
                            `;
                        },
                        weekDayName: model => {
                            const date = moment(model.dateInstance.toDate()).format("YYYY-MM-DD");
                            let isHoliday = model.day === 0 || model.day === 6;

                            const exception = this.store.holidayExceptions.find(holidayException => holidayException.date === date);
                            if (exception != null) {
                                ({ isHoliday } = exception);
                            }

                            return `
                                <span class=${isHoliday ?
                                    "day-weekend" :
                                    "day-workday"}>
                                <span class="toastui-calendar-day-name__date">${model.date}</span>
                                &nbsp;&nbsp;
                                <span class="toastui-calendar-day-name__name">${model.dayName}</span>
                                </span>
                            `;
                        },
                        monthGridHeader: model => {
                            let isHoliday = model.day === 0 || model.day === 6;

                            const exception = this.store.holidayExceptions.find(holidayException => holidayException.date === model.date);
                            if (exception != null) {
                                ({ isHoliday } = exception);
                            }

                            return `<span data-date=${model.date} class="day-name ${isHoliday ?
                                "day-weekend" :
                                "day-workday"}">${parseInt(model.date.split("-")[2], 10)}</span>`;
                        },
                    },
                },
            );

            this.initCalendarLgEventListeners();

            this.updateDate();
            this.updateMode();
        } catch(e) {
            console.error("Error in <initCalendarLg> ", e);
        }
    }

    private initCalendarLgEventListeners(): void {
        this.calendarLg.on("clickMoreEventsBtn", ({ date, target }) => {
            const eventsIdData = Array.from(target.querySelectorAll(".toastui-calendar-weekday-event-block")).map(element => ({
                eventId: element.getAttribute("data-event-id"),
                calendarId: element.getAttribute("data-calendar-id"),
            }));
            const events: CalendarEventObject[] = [];
            for (const eventIds of eventsIdData) {
                if (eventIds.eventId === null || eventIds.calendarId === null) {
                    continue;
                }
                const event = this.calendarLg.getEvent(eventIds.eventId, eventIds.calendarId) as CalendarEventObject | null;
                if (event !== null) {
                    events.push(event);
                }
            }

            const header = this.calendarLgRoot.querySelector(`.day-name[data-date='${moment(date).format("YYYY-MM-DD")}']`)
                ?.parentElement?.parentElement?.parentElement;
            const button = header?.querySelector(".toastui-calendar-grid-cell-more-events");
            const rect = button?.getBoundingClientRect();

            if (rect == null) {
                return;
            }

            this.showEventsPopup(
                {
                    x: rect.x,
                    y: rect.y,
                },
                events,
                date,
                () => {
                    this.closeCalendarLgMoreEventsPopup();
                },
            );
        });

        this.calendarLgRoot.addEventListener("click", event => {
            const target = event.target as HTMLElement;
            const button = target.closest(".toastui-calendar-grid-cell-more-events");
            if (button !== null) {
                setTimeout(() => {
                    const popupContainer = this.calendarLgRoot.querySelector<HTMLElement>(".toastui-calendar-see-more-container");
                    if (popupContainer === null) {
                        return;
                    }
                    if (popupContainer !== null) {
                        void computePosition(button, popupContainer, {
                            middleware: [
                                autoPlacement({
                                    alignment: "start",
                                }),
                            ],
                        }).then(({ x, y }) => {
                            popupContainer.style.left = `${x}px`;
                            popupContainer.style.top = `${y}px`;
                        });
                    }
                }, 0);
            }
        });

        this.calendarLg.on("clickDayName", ({ date }) => {
            this.calendarSm.set({
                selectedDates: [date],
            });
            this.store.onChange("date", moment(new Date(date)));
        });

        this.calendarLg.on("clickEvent", event => {
            if ([ScheduleCalendarType.week, ScheduleCalendarType.workweek].includes(this.store.mode)) {
                const events = this.getNextEvents(event.event as CalendarEventObject);
                if (events.length >= 4) {
                    this.showEventsPopup(
                        {
                            x: event.nativeEvent.clientX,
                            y: event.nativeEvent.clientY,
                        },
                        events,
                        (event.event as CalendarEventObject).raw.start,
                    );
                    return;
                }
            }

            this.handleEventClick(event.nativeEvent, event.event as CalendarEventObject, undefined, () => {
                this.closeCalendarLgMoreEventsPopup();
            });
        });

        this.shadowRoot
            ?.querySelector("#calendar-lg")
            ?.addEventListener("contextmenu", (e: MouseEvent) => {
                // Проверяем на клик в зоне события календаря
                const { eventId, calendarId } = this.getEventData(
                    e.target as Element,
                );
                if (eventId == null || calendarId === null) {
                    this.handleCalendarRightClick(e);
                    return;
                }

                const event = this.calendarLg.getEvent(eventId, calendarId) as CalendarEventObject;

                if ([ScheduleCalendarType.week, ScheduleCalendarType.workweek].includes(this.store.mode)) {
                    const events = this.getNextEvents(event);
                    if (events.length >= 4) {
                        this.showEventsPopup(
                            {
                                x: e.clientX,
                                y: e.clientY,
                            },
                            events,
                            event.raw.start,
                        );
                        return;
                    }
                }

                this.handleEventRightClick(e, event, undefined, () => {
                    this.closeCalendarLgMoreEventsPopup();
                });
            });
    }

    private handleCalendarRightClick(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();

        const date = this.getCalendarRightClickDate(event);

        if (date === null) {
            return;
        }

        const x = event.clientX;
        const y = event.clientY;

        const popover = new UiFloatPopover(
            calendarContextMenu(),
            {
                x,
                y,
            },
        );

        const currentDate = new Date();
        date.setHours(currentDate.getHours());
        date.setMinutes(currentDate.getMinutes());
        date.setSeconds(currentDate.getSeconds());

        popover.root.querySelector("#create")?.addEventListener("click", () => {
            this.modalManager.openModal(ModalID.AddSchedule, {
                initialDate: date,
            });

            popover.destroy();
        });
    }

    private getCalendarRightClickDate(event: MouseEvent): Date | null {
        const eventTarget = event.target as HTMLElement;
        if (this.store.mode === ScheduleCalendarType.month) {
            let date: string | null = null;
            if (eventTarget.classList.contains("day-name")) {
                date = eventTarget.getAttribute("data-date");
            } else {
                const dayELement = eventTarget.querySelector(".toastui-calendar-template-monthGridHeader > span");
                if (dayELement == null) {
                    return null;
                }
                date = dayELement.getAttribute("data-date");
            }
            return moment(date, "YYYY-MM-DD").toDate();
        }

        const column = eventTarget.closest(".toastui-calendar-column");
        if (column == null) {
            return null;
        }
        const weekday = parseInt(column.getAttribute("data-testid")?.split("-")[2] ?? "0", 10);
        const startDate = moment(this.calendarLg.getDateRangeStart().toDate());
        const date = startDate.isoWeekday(weekday === 0 ?
            7 :
            weekday).toDate();

        return date;
    }

    private handleEventClick(event: MouseEvent, eventObject: CalendarEventObject, parent?: HTMLElement, afterModalOpen?: () => void): void {
        event.stopPropagation();
        event.preventDefault();

        const x = event.clientX;
        const y = event.clientY;

        const eventId = eventObject.id;
        const scheduleItemId = eventId.split("-")[0];

        const scheduleItem = this.store.getScheduleById(parseInt(scheduleItemId, 10));

        if (scheduleItem === null) {
            return;
        }

        const startTime = moment(eventObject.raw.start).format(
            "dddd, D MMMM HH:mm",
        );

        const date: Date = moment(eventObject.raw.start).toDate();

        const item = scheduleItem.parentId !== null || isNonRepeatableItem(scheduleItem) ?
            scheduleItem :
            {
                ...scheduleItem,
                id: getTemporaryScheduleItemId(),
                parentId: scheduleItem.id,
                rrule: getSingleItemRRule(date),
            };

        const clickTime = new Date();

        // Проверяем на двойной клик
        if (
            this.lastClickedEventInfo !== null &&
            eventId === this.lastClickedEventInfo.id &&
            clickTime.getTime() - this.lastClickedEventInfo.time.getTime() < DOUBLE_CLICK_DELAY_MS
        ) {
            clearTimeout(this.eventClickTimeout ?? undefined);
            this.eventClickTimeout = null;
            this.lastClickedEventInfo = null;

            this.modalManager.openModal(
                ModalID.EditSchedule,
                {
                    scheduleItem: item,
                    isSingle: item.parentId !== null,
                    parentScheduleItem: item.parentId !== null ?
                        this.store.getScheduleById(item.parentId) ?? undefined :
                        undefined,
                    initialDateStart: date,
                },
            );
            afterModalOpen?.();

            return;
        }

        this.lastClickedEventInfo = {
            id: eventId,
            time: clickTime,
        };

        this.eventClickTimeout = setTimeout(() => {
            this.eventClickTimeout = null;
            this.lastClickedEventInfo = null;

            // This is how its written in VMInfo
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const node = window.$pr.getObjectById(scheduleItem.action.parameterId) as { _Name: string };

            const isParameterFound = node != null;
            const parameterName = node?._Name ?? "Параметр не найден";

            const popover = new UiFloatPopover(
                popupSelectEvent(scheduleItem, startTime, parameterName, isParameterFound),
                {
                    x,
                    y,
                },
                undefined,
                undefined,
                undefined,
                parent,
            );

            popover.root
                ?.querySelector("#close-icon")
                ?.addEventListener("click", () => popover.destroy());

            popover.root?.querySelector("#edit")?.addEventListener("click", () => {
                this.modalManager.openModal(
                    ModalID.EditSchedule,
                    {
                        scheduleItem: item,
                        isSingle: item.parentId !== null,
                        parentScheduleItem: item.parentId !== null ?
                            this.store.getScheduleById(item.parentId) ?? undefined :
                            undefined,
                        initialDateStart: date,
                    },
                );

                afterModalOpen?.();
                popover.destroy();
            });

            popover.root
                ?.querySelector("#delete")
                ?.addEventListener("click", (mouseEvent: MouseEvent) => {
                    showConfirmationPopup({
                        position: {
                            x: mouseEvent.clientX,
                            y: mouseEvent.clientY,
                        },
                        title: "Удалить",
                        description: "Вы уверены, что хотите удалить событие?",
                        onConfirm: () => {
                            if (scheduleItem.parentId !== null || isNonRepeatableItem(scheduleItem)) {
                                this.store.deleteScheduleById(scheduleItem.id);
                                void this.api.removeTaskById(scheduleItem.id);
                            } else {
                                this.excludeDateTimeFromItem(scheduleItem, date);
                            }
                            popover.destroy();
                            this.closeCalendarLgMoreEventsPopup();
                        },
                        parent: popover.root,
                    });
                });
        }, DOUBLE_CLICK_DELAY_MS);
    }

    private handleEventRightClick(
        event: MouseEvent,
        eventObject: CalendarEventObject,
        parent?: HTMLElement,
        afterModalOpen?: () => void,
    ): void {
        event.preventDefault();
        const x = event.clientX;
        const y = event.clientY;

        const date: Date = moment(eventObject.raw.start).toDate();

        const scheduleItemId = eventObject.id.split("-")[0];

        const scheduleItem = this.store.getScheduleById(parseInt(scheduleItemId, 10));

        if (scheduleItem && date) {
            setTimeout(() => {
                const recurrence = mapFromRRuleFrequency(scheduleItem.rrule.rrules()[0].options.freq);
                if (recurrence === ScheduleRecurrence.NoRepeat && scheduleItem.parentId === null) {
                    const actionsPopover = new UiFloatPopover(
                        eventActionsMenuPopup({
                            isEnabled: scheduleItem.enabled,
                            isSingle: true,
                        }),
                        {
                            x,
                            y,
                        },
                        undefined,
                        undefined,
                        undefined,
                        parent,
                    );
                    this.initEventActions(actionsPopover, false, scheduleItem, date, undefined, afterModalOpen);
                } else {
                    const popover = new UiFloatPopover(
                        popupContextMenuEvent(),
                        {
                            x,
                            y,
                        },
                        undefined,
                        undefined,
                        undefined,
                        parent,
                    );
                    this.initEventPopoverActions(popover, scheduleItem, date, afterModalOpen);
                }
            }, 0);
        }
    }

    private initEventPopoverActions(popover: UiFloatPopover, scheduleItem: ScheduleItem, date: Date, afterModalOpen?: () => void): void {

        const singleEventButton = popover.root.querySelector<HTMLLIElement>("#single-event");
        const rowEventsButton = popover.root.querySelector<HTMLLIElement>("#row-events");

        if (singleEventButton == null || rowEventsButton == null) {
            return;
        }

        this.initPopoverButtonAction(popover, singleEventButton, scheduleItem, true, date, afterModalOpen);
        this.initPopoverButtonAction(popover, rowEventsButton, scheduleItem, false, date, afterModalOpen);
    }

    private initPopoverButtonAction(
        popover: UiFloatPopover,
        button: HTMLElement,
        scheduleItem: ScheduleItem,
        isSingle: boolean,
        date: Date,
        afterModalOpen?: () => void,
    ): void {
        let isOverPopover = false;
        let isOverButton = false;

        let actionsPopover: UiFloatPopover | null = null;

        const isParentEnabled = (scheduleItem.parentId !== null ?
            this.store.getScheduleById(scheduleItem.parentId) :
            scheduleItem)?.enabled ?? false;

        const hidePopover = (): void => {
            if (!isOverPopover && !isOverButton) {
                actionsPopover?.destroy();
                actionsPopover = null;
            }
        };

        const openPopover = (): void => {
            if (actionsPopover !== null) {
                return;
            }

            actionsPopover = new UiFloatPopover(
                eventActionsMenuPopup({
                    isEnabled: isSingle ?
                        scheduleItem.enabled :
                        isParentEnabled,
                    isSingle,
                }),
                popover.root,
                ["right-start"],
                undefined,
                undefined,
                popover.root,
            );

            actionsPopover.root.addEventListener("mouseenter", () => {
                isOverPopover = true;
            });

            actionsPopover.root.addEventListener("mouseleave", () => {
                isOverPopover = false;
                hidePopover();
            });

            this.initEventActions(actionsPopover, isSingle, scheduleItem, date, popover, afterModalOpen);
        };

        button.addEventListener("mouseenter", () => {
            openPopover();
            isOverButton = true;
        });

        button.addEventListener("mouseleave", () => {
            isOverButton = false;
            setTimeout(hidePopover, 100);
        });
    }

    private initEventActions(
        popover: UiFloatPopover,
        isSingle: boolean,
        scheduleItem: ScheduleItem,
        date: Date,
        parentPopover?: UiFloatPopover,
        afterModalOpen?: () => void,
    ): void {
        const editButton = popover.root.querySelector("#edit");
        const deleteButton = popover.root.querySelector("#delete");
        const enableButton = popover.root.querySelector("#enable");

        editButton?.addEventListener("click", () => {
            let item: ScheduleItem | null = null;

            if (isSingle) {
                if (scheduleItem.parentId !== null) {
                    item = scheduleItem;
                } else {
                    item = {
                        ...scheduleItem,
                        id: getTemporaryScheduleItemId(),
                        parentId: scheduleItem.id,
                        rrule: getSingleItemRRule(date),
                    };
                }
            } else if (scheduleItem.parentId !== null) {
                item = this.store.getScheduleById(scheduleItem.parentId);
            } else {
                item = scheduleItem;
            }

            if (item === null) {
                return;
            }

            this.modalManager.openModal(
                ModalID.EditSchedule,
                {
                    scheduleItem: item,
                    isSingle,
                    parentScheduleItem: isSingle && item.parentId !== null ?
                        this.store.getScheduleById(item.parentId) ?? undefined :
                        undefined,
                    initialDateStart: date,
                },
            );

            afterModalOpen?.();
            popover.destroy();
            parentPopover?.destroy();
        });

        deleteButton?.addEventListener("click", (event: MouseEvent) => {
            showConfirmationPopup({
                position: {
                    x: event.clientX,
                    y: event.clientY,
                },
                title: "Удалить",
                description: `Вы уверены, что хотите удалить ${isSingle || isNonRepeatableItem(scheduleItem) ?
                    "событие" :
                    "ряд событий"}?`,
                parent: popover.root,
                onConfirm: () => {
                    if (isSingle) {
                        if (scheduleItem.parentId !== null) {
                            this.store.deleteScheduleById(scheduleItem.id);
                            void this.api.removeTaskById(scheduleItem.id);
                        } else {
                            this.excludeDateTimeFromItem(scheduleItem, date);
                        }

                    } else {
                        const id = scheduleItem.parentId ?? scheduleItem.id;

                        const childSchedules = this.store.getChildSchedules(id);
                        childSchedules.forEach(child => {
                            this.store.deleteScheduleById(child.id);
                            void this.api.removeTaskById(child.id);
                        });

                        this.store.deleteScheduleById(id);
                        void this.api.removeTaskById(id);
                    }
                    afterModalOpen?.();
                    this.closeCalendarLgMoreEventsPopup();
                    popover.destroy();
                    parentPopover?.destroy();
                },
            });
        });

        enableButton?.addEventListener("click", (event: MouseEvent) => {

            const isExceptionItem = scheduleItem.parentId !== null;
            const item = isExceptionItem && !isSingle ?
                this.store.getScheduleById(scheduleItem.parentId) :
                scheduleItem;

            if (item === null) {
                return;
            }

            const isActivated = item.enabled;
            const isSingleEvent = isSingle || isNonRepeatableItem(item);

            const description = `Вы уверены, что хотите ${isActivated ?
                "деактивировать" :
                "активировать"} ${isSingleEvent ?
                "событие" :
                "все события ряда"}?`;

            showConfirmationPopup({
                position: {
                    x: event.clientX,
                    y: event.clientY,
                },
                title: isActivated ?
                    "Деактивация события" :
                    "Активация события",
                description,
                parent: popover.root,
                onConfirm: () => {
                    if (isSingle) {
                        if (scheduleItem.parentId !== null) {
                            const newScheduleItem: ScheduleItem = {
                                ...scheduleItem,
                                enabled: !scheduleItem.enabled,
                            };
                            this.store.updateSchedule(newScheduleItem);
                            void this.api.updateTask(newScheduleItem);
                        } else {
                            const newScheduleItem: ScheduleItem = {
                                ...scheduleItem,
                                id: getTemporaryScheduleItemId(),
                                parentId: scheduleItem.id,
                                enabled: !scheduleItem.enabled,
                                rrule: getSingleItemRRule(date),
                            };
                            void this.addScheduleItem(newScheduleItem);
                            this.excludeDateTimeFromItem(scheduleItem, date);
                        }
                    } else {
                        const isExceptionItem_ = scheduleItem.parentId !== null;
                        const item_ = isExceptionItem_ ?
                            this.store.getScheduleById(scheduleItem.parentId) :
                            scheduleItem;

                        if (item_ === null) {
                            return;
                        }

                        if (isExceptionItem_) {
                            const childSchedules = this.store.getChildSchedules(item_.id);
                            childSchedules.forEach(child => {
                                this.store.deleteScheduleById(child.id);
                                void this.api.removeTaskById(child.id);
                            });
                        }
                        const newItem = {
                            ...item_,
                            enabled: !item_.enabled,
                            rrule: isExceptionItem_ ?
                                this.getRRuleWithoutExceptions(item_) :
                                item_.rrule,
                        };
                        this.store.updateSchedule(newItem);
                        void this.api.updateTask(newItem);
                    }

                    afterModalOpen?.();
                    popover.destroy();
                    parentPopover?.destroy();
                },
            });
        });
    }

    private closeCalendarLgMoreEventsPopup(): void {
        const overlay = this.calendarLgRoot.querySelector<HTMLDivElement>(".toastui-calendar-popup-overlay");
        if (overlay !== null) {
            overlay.click();
        }
    }

    private getNextEvents(targetEvent: CalendarEventObject): CalendarEventObject[] {
        const result: CalendarEventObject[] = [targetEvent];

        const targetStart = moment(targetEvent.start.toDate());
        const targetEnd = moment(targetEvent.end.toDate());

        const isNext = (event: CalendarEventObject): boolean => {
            const eventStart = moment(event.start.toDate());
            const eventEnd = moment(event.end.toDate());
            if (!targetStart.isSame(eventStart, "day")) {
                return false;
            }

            const startDifference = targetStart.diff(eventStart, "minutes");
            const endDifference = targetEnd.diff(eventEnd, "minutes");

            return startDifference <= 30 && startDifference >= 0 || endDifference >= -30 && endDifference <= 0;
        };

        for (const calendarEvent of this.currentCalendarEvents) {
            if (calendarEvent.id === targetEvent.id) {
                continue;
            }
            if (isNext(calendarEvent)) {
                const event = this.calendarLg.getEvent(calendarEvent.id, targetEvent.calendarId) as CalendarEventObject;
                if (event) {
                    result.push(event);
                }
            }
        }

        return result;
    }

    private showEventsPopup(
        position: { x: number; y: number },
        events: CalendarEventObject[],
        date: Date,
        afterModalOpen?: () => void,
    ): void {
        setTimeout(() => {
            const popover = new UiFloatPopover(
                popupEventsList(date),
                {
                    x: position.x,
                    y: position.y,
                },
                undefined,
                undefined,
                () => {
                    this.closeCalendarLgMoreEventsPopup();
                },
                undefined,
                {
                    initialMaxHeight: "260px",
                },
                true,
            );

            const listElement = popover.root.querySelector(".events-list");

            [...events]
                .sort((event1, event2) => {
                    const result = moment(event1.raw.start).format("HH:mm") > moment(event2.raw.start).format("HH:mm") ?
                        1 :
                        -1;
                    return result;
                })
                .forEach(event => {
                    const itemElement = document.createElement("li");
                    const { scheduleItem } = event.raw;
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const node = window.$pr.getObjectById(scheduleItem.action.parameterId) as { _Name: string };

                    const isParameterFound = node != null;
                    const parameterName = node?._Name ?? "Параметр не найден";
                    itemElement.innerHTML = `
                        <div class="item__marker"></div>
                        <span>${moment(event.raw.start).format("HH:mm")}, ${event.body}, <span ${isParameterFound ?
                            "" :
                            "class='parameter-not-found'"}>${parameterName}</span>
                        </span>
                    `;
                    itemElement.classList.add("item");
                    itemElement.style.setProperty("--marker-color", event.backgroundColor);

                    itemElement.addEventListener("click", e => {
                        this.handleEventClick(e, event, popover.root, () => {
                            popover.destroy();
                            afterModalOpen?.();
                        });
                    });
                    itemElement.addEventListener("contextmenu", e => {
                        this.handleEventRightClick(e, event, popover.root, () => {
                            popover.destroy();
                            afterModalOpen?.();
                        });
                    });

                    listElement?.appendChild(itemElement);
                });
        }, 0);
    }

    private excludeDateTimeFromItem(item: ScheduleItem, date: Date): void {
        const newRRuleSet = item.rrule.clone();
        newRRuleSet.exdate(convertDateToUtc(date));
        const newItem: ScheduleItem = {
            ...item,
            rrule: newRRuleSet,
        };
        this.store.updateSchedule(newItem);
        void this.api.updateTask(newItem);
    }

    private getRRuleWithoutExceptions(item: ScheduleItem): RRuleSet {
        const newRRuleSet = new RRuleSet();
        const rrule = item.rrule.rrules()[0];
        newRRuleSet.rrule(rrule);
        return newRRuleSet;
    }

    private clearExcludedDatesFromItem(item: ScheduleItem): void {
        const newRRuleSet = this.getRRuleWithoutExceptions(item);
        const newItem: ScheduleItem = {
            ...item,
            rrule: newRRuleSet,
        };
        this.store.updateSchedule(newItem);
        void this.api.updateTask(newItem);
    }

    private async addScheduleItem(item: ScheduleItem): Promise<void> {
        const schedule = this.selectedSchedule;
        if (schedule == null) {
            return;
        }

        this.store.addSchedule(item);
        try {
            const taskId = await this.api.addScheduleItem({
                item,
                scheduleId: schedule.scheduleId,
                path: schedule.path,
                fullPath: schedule.fullPath,
            });
            if (taskId) {
                this.store.replaceIdOfScheduleItem(item.id, taskId);
            }
        } catch(e) {
            console.error(e);
        }
    }

    private onAddScheduleModalSave(item: ScheduleItem): void {
        void this.addScheduleItem(item);
    }

    private onEditScheduleModalSave(item: ScheduleItem, isSingle: boolean, initialItem: ScheduleItem, initialDate: Date): void {
        if (isSingle) {
            const isItemExists = this.store.hasScheduleItemWithId(item.id);
            if (!isItemExists) {
                void this.addScheduleItem(item);
                this.excludeDateTimeFromItem(initialItem, initialDate);
            } else {
                this.store.updateSchedule(item);
                void this.api.updateTask(item);
            }
        } else {
            const childSchedules = this.store.getChildSchedules(item.id);
            if (childSchedules.length > 0) {
                childSchedules.forEach(child => {
                    this.store.deleteScheduleById(child.id);
                    void this.api.removeTaskById(child.id);
                });
                this.clearExcludedDatesFromItem(item);
            } else {
                this.store.updateSchedule(item);
                void this.api.updateTask(item);
            }
        }

        const editModal = this.modalManager.getModal(ModalID.EditSchedule) as EditScheduleModal;
        editModal.resetState();
    }

    private onEditScheduleModalDelete(scheduleItem: ScheduleItem, isSingle: boolean, date: Date): void {
        if (isSingle) {
            if (this.store.hasScheduleItemWithId(scheduleItem.id)) {
                this.store.deleteScheduleById(scheduleItem.id);
                void this.api.removeTaskById(scheduleItem.id);
            } else {
                const item = scheduleItem.parentId !== null ?
                    this.store.getScheduleById(scheduleItem.parentId) :
                    null;

                if (item !== null) {
                    this.excludeDateTimeFromItem(item, date);
                }
            }
        } else {
            const id = scheduleItem.parentId ?? scheduleItem.id;
            const childSchedules = this.store.getChildSchedules(id);
            childSchedules.forEach(child => {
                this.store.deleteScheduleById(child.id);
                void this.api.removeTaskById(child.id);
            });
            this.store.deleteScheduleById(id);
            void this.api.removeTaskById(id);
        }
    }

    private initModals(): void {
        this.modalManager.addModal(
            ModalID.AddSchedule,
            new AddScheduleModal(
                this.store,
                this.onAddScheduleModalSave.bind(this),
            ),
            "Новое событие",
        );

        this.modalManager.addModal(
            ModalID.EditSchedule,
            new EditScheduleModal(this.onEditScheduleModalSave.bind(this), this.onEditScheduleModalDelete.bind(this)),
            "Редактировать событие",
        );

        this.modalManager.addModal(
            ModalID.Holidays,
            new HolidaysModal(this.store, this.api),
            "Нерабочие дни",
        );
    }

    private onStoreChange(): void {
        this.disposer = observe(this.store, change => {
            if (change.type !== "update") {
                return;
            }
            if (change.name === "date") {
                return this.updateDate();
            }
            if (change.name === "schedules") {
                return this.populateSchedule();
            }
            if (change.name === "mode") {
                this.updateMode();
                this.populateSchedule();
            }
            if (change.name === "holidayExceptions") {
                this.exceptionHolidays.clear();
                this.exceptionWorkdays.clear();
                this.store.holidayExceptions.forEach(exception => {
                    if (exception.isHoliday) {
                        this.exceptionHolidays.add(exception.date);
                    } else {
                        this.exceptionWorkdays.add(exception.date);
                    }
                });
                this.updateCalendarHolidays();
                this.populateSchedule();
            }
        });
    }

    private controlDate(e: "prev" | "next"): void {
        const date = this.store.date.clone();
        switch (CALENDAR_TYPE_TO_VIEW_MAP[this.store.mode]) {
            case "month": {
                if (e === "prev") {
                    date.subtract(1, "month");

                } else {
                    date.add(1, "month");
                }
                break;
            }
            case "week": {
                if (e === "prev") {
                    date.subtract(7, "day");
                } else {
                    date.add(7, "day");
                }
                break;
            }
            case "day":
            default: {
                if (e === "prev") {
                    date.subtract(1, "day");
                } else {
                    date.add(1, "day");
                }
                break;
            }
        }
        this.store.onChange("date", date);
    }

    /**
     * Handles schedule change.
     * @param event Event.
     */
    protected onScheduleChange(event: Event): void {
        const { value } = event.target as HTMLSelectElement;
        const selectedScheduleId = parseInt(value, 10);
        const schedule = this.schedules.find(schedule_ => schedule_.scheduleId === selectedScheduleId);
        if (schedule) {
            this.selectedSchedule = schedule;
        }
    }

    /**
     * Updates schedule state.
     * @param state State.
     */
    protected async updateScheduleState({ isLocked, isEnabled }: { isLocked: boolean; isEnabled: boolean }): Promise<void> {
        const schedule = this.selectedSchedule;
        if (schedule != null) {
            await this.api.updateScheduleState({
                scheduleId: schedule.scheduleId,
                path: schedule.path,
                enabled: isEnabled,
                locked: isLocked,
            });

            const newSchedules = [...this.schedules.map(item => ({ ...item }))];
            const currentSchedule = newSchedules.find(item => item.scheduleId === schedule.scheduleId);
            if (currentSchedule) {
                currentSchedule.isEnabledHmi = isEnabled ?? currentSchedule.isEnabledHmi;
                currentSchedule.isLocked = isLocked ?? currentSchedule.isLocked;
                this.selectedSchedule = currentSchedule;
            }

            this.schedules = this.sortSchedules(newSchedules);
        }
    }

    /** Handles task edited warning close button click. */
    protected handleTaskEditedWarningCloseButtonClick(): void {
        this.taskEditedWarningType = "none";
    }

    /** Handles schedule activation lock change. */
    protected handleScheduleActivationLockChange(): void {
        const schedule = this.selectedSchedule;
        void this.updateScheduleState({
            isLocked: !schedule.isLocked,
            isEnabled: schedule.isEnabledFb,
        });
    }

    /**
     * Handles schedule enable state change.
     * @param isEnabled Is enabled.
     */
    protected handleScheduleEnabledChange(isEnabled: boolean): void {
        const schedule = this.selectedSchedule;
        void this.updateScheduleState({
            isEnabled,
            isLocked: schedule.isLocked,
        });
    }

    /** Handles weekends button click. */
    protected onWeekendsButtonClick(): void {
        this.modalManager.openModal(ModalID.Holidays);
    }

    /** Handles previous button click. */
    protected onPrevButtonClick(): void {
        this.controlDate("prev");
    }

    /** Handles next button click. */
    protected onNextButtonClick(): void {
        this.controlDate("next");
    }

    private initMomentLocale(): void {
        moment.locale(MOMENT_LOCALE);
    }

    private initActions(): void {
        const button = this.renderRoot.querySelector<HTMLButtonElement>("#add-schedule-button");

        if (button === null) {
            return;
        }

        this.addScheduleButton = button;

        this.addScheduleButton.addEventListener("click", () => {
            const selectedDate = this.calendarSm.context.selectedDates.at(0);
            let date: moment.Moment | Date | undefined;
            if (selectedDate != null) {
                date = moment(new Date(selectedDate));
                date = date.toDate();
                const currentDate = new Date();
                date.setHours(currentDate.getHours());
                date.setMinutes(currentDate.getMinutes());
                date.setSeconds(currentDate.getSeconds());
            }
            this.modalManager.openModal(ModalID.AddSchedule, {
                initialDate: date instanceof Date ?
                    date :
                    undefined,
            });
        });
    }

    private updateMode(): void {
        const { mode } = this.store;
        if (mode === ScheduleCalendarType.today) {
            this.store.onChange("date", moment());
        }
        if (mode === ScheduleCalendarType.day) {
            const selectedDate = this.calendarSm.context.selectedDates.at(0);
            if (selectedDate) {
                this.store.onChange("date", moment(new Date(selectedDate)));
            }
        }
        if (mode === ScheduleCalendarType.week) {
            this.calendarLg.setOptions({
                week: { workweek: false },
            });
        }
        if (mode === ScheduleCalendarType.workweek) {
            this.calendarLg.setOptions({
                week: { workweek: true },
            });
        }

        const listView = this.renderRoot.querySelectorAll(
            "#calendar-view-container li",
        );
        listView.forEach(el => {
            el.classList.remove("active");
        });

        const targetItem = this.renderRoot.querySelector(
            `#calendar-view-container li[view="${mode}"]`,
        );

        if (targetItem) {
            targetItem.classList.add("active");
        }

        this.calendarLg.changeView(CALENDAR_TYPE_TO_VIEW_MAP[mode]);
        this.updateHeaderTime();
    }

    private updateDate(): void {
        if (this.store.mode === ScheduleCalendarType.today) {
            const isToday = this.store.date.isSame(moment(), "day");
            if (!isToday) {
                this.store.onChange("mode", ScheduleCalendarType.day);
            }
        }
        this.updateHeaderTime();
        this.populateSchedule();

        this.calendarLg.setDate(this.store.date.toString());
    }

    private updateHeaderTime(): void {
        const date = this.store.date.clone();
        switch (this.store.mode) {
            case ScheduleCalendarType.month:
                this.headerTime = date.format("MMMM YYYY");
                break;
            case ScheduleCalendarType.week: {
                const monday = date.clone().startOf("isoWeek")
                    .format("l");
                const sunday = date.clone().endOf("isoWeek")
                    .format("l");
                this.headerTime = `${monday} - ${sunday}`;
                break;
            }
            case ScheduleCalendarType.workweek: {
                const monday = date.clone().startOf("isoWeek")
                    .format("l");
                const friday = date.clone().isoWeekday(5)
                    .format("l");
                this.headerTime = `${monday} - ${friday}`;
                break;
            }
            case ScheduleCalendarType.day:
            case ScheduleCalendarType.today:
            default: {
                this.headerTime = date.format("l");
                break;
            }
        }
    }

    private getStartEndDates(): [Date, Date] {
        const date = this.store.date.clone();
        switch (this.store.mode) {
            case ScheduleCalendarType.month: {
                const start = date.clone().startOf("month")
                    .utc()
                    .toDate();
                const end = date.clone().endOf("month")
                    .utc()
                    .toDate();
                return [start, end];
            }
            case ScheduleCalendarType.week: {
                const start = date.clone().startOf("isoWeek")
                    .utc()
                    .toDate();
                const end = date.clone().endOf("isoWeek")
                    .utc()
                    .toDate();
                return [start, end];
            }
            case ScheduleCalendarType.workweek: {
                const start = date.clone().startOf("isoWeek")
                    .utc()
                    .toDate();
                const end = date.clone().isoWeekday(5)
                    .endOf("day")
                    .utc()
                    .toDate();
                return [start, end];
            }
            case ScheduleCalendarType.day:
            case ScheduleCalendarType.today:
            default: {
                const start = date.clone().startOf("day")
                    .utc()
                    .toDate();
                const end = date.clone().endOf("day")
                    .utc()
                    .toDate();
                return [start, end];
            }
        }
    }

    private initViewHandler(): void {
        const container = this.renderRoot.querySelector("#calendar-view-container");
        if (!container) {
            return;
        }
        container.addEventListener("click", e => {
            const tag = (e.target as HTMLElement).tagName;
            if (tag !== "LI") {
                return;
            }

            const view = (e.target as HTMLElement).getAttribute("view") as ScheduleCalendarType;
            this.store.onChange("mode", view);
        });
    }

    private populateSchedule(): void {
        const schedule = this.generateSchedule();
        this.currentCalendarEvents = schedule;
        this.calendarLg.clear();
        this.calendarLg.createEvents(schedule);
    }

    private generateSchedule(): CalendarEventObject[] {
        const { schedules } = this.store;

        if (schedules == null) {
            return [];
        }

        const result: CalendarEventObject[] = [];

        const [start1, end1] = this.getStartEndDates();

        const start = convertDateToUtc(start1);
        const end = convertDateToUtc(end1);

        schedules.forEach(schedule => {

            const rruleClone = schedule.rrule.clone();

            const startDate = moment.utc(rruleClone.rrules()[0].options.dtstart);
            const startTime = moment.utc(rruleClone.rrules()[0].options.dtstart).format("HHmmss");

            if (schedule.userSettings.execute === "weekends") {
                this.exceptionHolidays.forEach(value => {
                    if (startDate.isSameOrBefore(moment.utc(value))) {
                        const date = moment.utc(`${value} ${startTime}`, "YYYY-MM-DD HHmmss").toDate();
                        rruleClone.rdate(date);
                    }
                });
                this.exceptionWorkdays.forEach(value => {
                    const date = moment.utc(`${value} ${startTime}`, "YYYY-MM-DD HHmmss").toDate();
                    rruleClone.exdate(date);
                });
            } else if (schedule.userSettings.execute === "weekdays") {
                this.exceptionWorkdays.forEach(value => {
                    if (startDate.isSameOrBefore(moment.utc(value))) {
                        const date = moment.utc(`${value} ${startTime}`, "YYYY-MM-DD HHmmss").toDate();
                        rruleClone.rdate(date);
                    }
                });
                this.exceptionHolidays.forEach(value => {
                    const date = moment.utc(`${value} ${startTime}`, "YYYY-MM-DD HHmmss").toDate();
                    rruleClone.exdate(date);
                });
            }

            const dates = rruleClone.between(start, end, true)
                .map(date => new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000));

            dates.forEach((date, index) => {
                const startDateMoment = moment(date);

                let endDateMoment = moment(date).add({
                    minutes: 30,
                });

                const breakpoint = startDateMoment.clone();
                breakpoint.set({
                    hours: 23,
                    minutes: 30,
                    seconds: 0,
                    milliseconds: 0,
                });

                if (startDateMoment.isAfter(breakpoint)) {
                    startDateMoment.set({
                        hours: 23,
                        minutes: 30,
                        seconds: 0,
                        milliseconds: 0,
                    });
                    endDateMoment = startDateMoment.clone().endOf("day");
                }
                const eventStartDate = startDateMoment.toDate();
                const eventEndDate = endDateMoment.toDate();

                const color = schedule.enabled ?
                    schedule.color :
                    "rgba(128,128,128,1)";

                result.push({
                    id: `${schedule.id.toString()}-${index}`,
                    body: schedule.name,
                    calendarId: "1",
                    category: "time",
                    dueDateClass: "",
                    end: new TZDate(eventEndDate),
                    start: new TZDate(eventStartDate),
                    title: "Событие",
                    backgroundColor: color,
                    borderColor: color,
                    raw: {
                        start: date,
                        scheduleItem: schedule,
                    },
                    customStyle: {
                        backgroundColor: color,
                    },
                });
            });
        });

        return result;
    }

    /**
     * Gets event data.
     * @param target Target element.
     */
    public getEventData(target: Element): { calendarId: null | string; eventId: null | string } {
        let eventElement: Element | null = null;

        // Выбор события на дневном/недельном тайм-фрейме
        if (target.closest(".toastui-calendar-event-time")) {
            eventElement = target.closest(".toastui-calendar-event-time");
        }

        // Выбор события на месячном тайм-фрейме (в списке детализации событий)

        if (target.closest(".toastui-calendar-weekday-event-block")) {
            eventElement = target.closest(".toastui-calendar-weekday-event-block");
        }

        // Обработка клика по событию на дневном/недельном тамй-фрейме
        if (eventElement) {
            return {
                calendarId: eventElement.getAttribute("data-calendar-id"),
                eventId: eventElement.getAttribute("data-event-id"),
            };
        }

        return { calendarId: null, eventId: null };
    }

    private updateCalendarHolidays(): void {
        const dateElements = this.calendarSmRoot.querySelectorAll(".vc-date");
        const holidayDates: string[] = [];

        dateElements.forEach(element => {
            const date = element.getAttribute("data-vc-date");
            const weekday = element.getAttribute("data-vc-date-week-day");
            if (weekday === null || date === null) {
                return;
            }
            const isDefaultWeekday = DEFAULT_WEEKDAY_NUMBERS.includes(weekday);
            if (isDefaultWeekday && !this.exceptionWorkdays.has(date) || this.exceptionHolidays.has(date)) {
                holidayDates.push(date);
            }
        });
        this.calendarSm.set({
            selectedHolidays: holidayDates,
        }, {
            dates: false,
            month: false,
            year: false,
            time: false,
            locale: false,
        });
    }
}
